import { DocumentType } from '@synergy/models';
import { FormattedDate, FormattedMessage } from 'react-intl';
import { PaloColumn, PaloFilterChips, PaloListView, PaloRowAction, PaloStatusPill } from '@synergy/components-palo';
import { PoolDocumentTitle } from '@synergy/components-next';
import { ReactElement, useEffect, useState } from 'react';
import { getDocumentStatusTranslationId } from '@synergy/utils';
import { useNavigate } from 'react-router';

import { ContactPresenter } from '../../../../common/components/contact-presenter/contact-presenter';
import { ConverterReceiptModal } from '../../../convert-receipt/converter-receipt-modal';
import { Inquiry } from '../../../inquiries/models/inquiry';
import { OfferTransition } from '../../../convert-receipt/models/offer/offerTransition';
import { PaloDocumentNumberCell } from '../../../../common/components/palo/palo-document-number-cell';
import { PaloNameListCell } from '../../../../common/components/palo/palo-name-list-cell';
import { PaloOfferCountCell } from '../../../inquiries/palo/palo-offer-count-cell';
import { Transition } from '../../../convert-receipt/models/type-transition';
import {
    deleteInquiry,
    duplicateInquiry,
    fetchInquiry,
    requestEnd as inquiryRequestEnd,
    resetInquiryState,
} from '../../../inquiries/inquiry-actions';
import { fetchTransitions, resetTransitions } from '../../../../common/actions/transitions-activity-actions';
import { inquiryPartners } from '../../../../common/utils/locked-partner-notice';
import { requestingSelector } from '../../store/dashboard-selectors';
import { transitionInquiryIntoPurchaseOrders } from '../../../convert-receipt/transition-actions';
import { useAppSelector } from '../../../../store/use-appstate-selector';
import { useDashboardSort } from '../../palo/use-dashboard-sort';
import { useStatusNames } from '../../../../common/components/filter/document-status-filter';
import { useThunkDispatch } from '../../../../store/use-thunk-dispatch';

const columns: PaloColumn<Inquiry>[] = [
    {
        key: 'number',
        type: 'documentNumber',
        sortKey: 'number',
        head: <FormattedMessage id="app.table.column.number" />,
        cell: item => <PaloDocumentNumberCell number={item.number} alert={item.receiptAlert} />,
    },
    {
        key: 'status',
        type: 'status',
        sortKey: 'status',
        head: <FormattedMessage id="app.table.column.status" />,
        cell: item => (
            <PaloStatusPill
                status={item.status}
                label={<FormattedMessage id={getDocumentStatusTranslationId(item.status)} />}
            />
        ),
    },
    {
        // As in the inquiry list (GF 30.10.2026, #2340).
        key: 'offers',
        sortKey: 'offers',
        type: 'count',
        head: <FormattedMessage id="app.table.column.offers" />,
        cell: item => <PaloOfferCountCell count={item.offerCount} />,
    },
    {
        key: 'title',
        type: 'name',
        sortKey: 'title',
        head: <FormattedMessage id="app.table.column.title" />,
        cell: item => item.title,
    },
    {
        key: 'customer',
        type: 'name',
        sortKey: 'accountPurchase',
        head: <FormattedMessage id="app.table.column.customer" />,
        cell: item => (
            <PaloNameListCell
                names={item.accountPurchases.map(purchase => purchase.customerAccount?.name)}
                companies={item.accountPurchases.map(purchase => purchase.customerAccount)}
                kind="customer"
            />
        ),
    },
    {
        key: 'responsibleContact',
        type: 'name',
        sortKey: 'responsibleContact',
        head: <FormattedMessage id="app.common.responsibleContact" />,
        cell: item => (
            <ContactPresenter
                person={item.responsibleContact}
                fullName={item.responsibleContact?.fullName}
                isDeleted={item.responsibleContact?.isDeleted}
                isLocked={item.responsibleContact?.isLocked}
            />
        ),
    },
    {
        key: 'createdAt',
        type: 'date',
        sortKey: 'createdAt',
        head: <FormattedMessage id="app.table.column.createdAt" />,
        cell: item => <FormattedDate value={item.createdAt} year="numeric" month="short" day="2-digit" />,
    },
    {
        key: 'endDate',
        type: 'date',
        sortKey: 'endDate',
        head: <FormattedMessage id="app.table.column.endDate" />,
        cell: item => item.endDate && <FormattedDate value={item.endDate} year="numeric" month="short" day="2-digit" />,
    },
];

export const DashboardOverviewInquiries = (): ReactElement => {
    const statusNames = useStatusNames('inquiries');
    const navigate = useNavigate();
    const { sort, onSortChange } = useDashboardSort('inquiries');
    const dispatch = useThunkDispatch();
    const [isConverterVisible, setIsConverterVisible] = useState(false);
    const inquiry = useAppSelector(state => state.inquiries.entity);
    const inquiries = useAppSelector(state => state.inquiries.flat);
    const requesting = useAppSelector(requestingSelector);

    const cleanupState = (): void => {
        dispatch(resetInquiryState());
        dispatch(inquiryRequestEnd());
    };

    useEffect(() => {
        return cleanupState;
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const handleConvertOpen = (id: number): void => {
        dispatch(resetInquiryState());
        dispatch(resetTransitions());
        dispatch(fetchTransitions(DocumentType.Inquiry, id));
        dispatch(fetchInquiry(id));
        setIsConverterVisible(true);
    };

    const handleTransition = (transitionObject: Transition) =>
        dispatch(transitionInquiryIntoPurchaseOrders(transitionObject as OfferTransition));

    const handleConvertClose = (): void => {
        cleanupState();
        setIsConverterVisible(false);
    };

    const rowActions = (item: Inquiry): PaloRowAction[] => [
        { label: 'Öffnen', onClick: () => navigate('/inquiries/' + item.id) },
        { label: 'Umwandeln', onClick: () => handleConvertOpen(item.id) },
        { label: 'Duplizieren', onClick: () => dispatch(duplicateInquiry(item.id, inquiryPartners(item))) },
        { label: 'Löschen', onClick: () => dispatch(deleteInquiry(item.id)) },
    ];

    return (
        <>
            <PoolDocumentTitle titleTranslationId="app.navigation.dashboard.inquiries" />
            <PaloFilterChips names={{ status: statusNames }} />
            <PaloListView<Inquiry>
                embedded
                columns={columns}
                sort={sort}
                onSortChange={onSortChange}
                items={inquiries.items}
                getRowId={item => item.id}
                requesting={requesting}
                emptyLabel="Keine Anfragen."
                rowHref={item => '/inquiries/' + item.id}
                rowActions={rowActions}
            />
            {inquiry && (
                <ConverterReceiptModal
                    isVisible={isConverterVisible}
                    source={inquiry}
                    onClose={handleConvertClose}
                    onConvert={handleTransition}
                />
            )}
        </>
    );
};
